<script setup>
import { computed } from 'vue'
import HistoricalTankChart from './HistoricalTankChart.vue'

const props = defineProps({
  tanks: { type: Array, default: () => [] },
  selectedTankNumber: { type: Number, default: null },
  series: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
  emptyMessage: { type: String, default: 'Дані відсутні' },
})
const emit = defineEmits(['select', 'retry'])

const tank = computed(() => props.tanks.find((item) => item.tankNumber === props.selectedTankNumber) ?? null)
const number = (value, sign = false) => {
  if (!Number.isFinite(value)) return '—'
  const formatted = new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 2 }).format(value)
  return `${sign && value > 0 ? '+' : ''}${formatted} л`
}
const capacity = computed(() => Number.isFinite(tank.value?.capacityLiters)
  ? `${number(tank.value.capacityLiters)} місткість` : 'Автошкала · місткість не налаштовано')
</script>

<template>
  <section class="indicator-section historical-tank-section" aria-labelledby="historical-tanks-heading">
    <h2 id="historical-tanks-heading"><span>03</span> Змінні залишки в резервуарах</h2>
    <div class="dashboard-panel historical-tank-panel">
      <template v-if="tanks.length">
        <div class="historical-tank-toolbar">
          <label>
            <span>Резервуар</span>
            <select :value="selectedTankNumber ?? ''" :disabled="loading" @change="emit('select', Number($event.target.value))">
              <option v-for="item in tanks" :key="item.tankNumber" :value="item.tankNumber">№{{ item.tankNumber }} · {{ item.productName || 'Невідомий нафтопродукт' }}<template v-if="item.productId != null"> · код {{ item.productId }}</template></option>
            </select>
          </label>
          <div v-if="tank" class="historical-tank-identity"><strong>{{ tank.productName || 'Невідомий нафтопродукт' }}</strong><span>Код {{ tank.productId ?? '—' }} · {{ capacity }}</span></div>
        </div>

        <div v-if="tank" class="historical-tank-metrics" aria-label="Показники вибраного резервуара">
          <article><small>На початок</small><strong>{{ number(tank.startLiters) }}</strong></article>
          <article><small>На кінець</small><strong>{{ number(tank.endLiters) }}</strong></article>
          <article class="metric-delivery"><small>Надходжень</small><strong>{{ number(tank.deliveredLiters) }}</strong></article>
          <article><small>Реалізація</small><strong>{{ number(tank.soldLiters) }}</strong></article>
          <article :class="{ 'metric-negative': tank.netChangeLiters < 0, 'metric-positive': tank.netChangeLiters > 0 }"><small>Зміна</small><strong>{{ number(tank.netChangeLiters, true) }}</strong></article>
        </div>

        <div v-if="loading" class="chart-loading" role="status">Будуємо історію резервуара…</div>
        <div v-else-if="error" class="chart-error" role="alert"><p>{{ error }}</p><button type="button" class="button secondary" @click="emit('retry')">Спробувати ще раз</button></div>
        <template v-else-if="series?.events?.length">
          <div v-if="series.sampled" class="chart-warning" role="status"><strong>Історію проріджено.</strong> Усі надходження збережено, частину точок реалізації відібрано для читабельності.</div>
          <div v-if="series.warnings?.length" class="chart-warning" role="status"><p v-for="warning in series.warnings" :key="warning.code">{{ warning.message }}</p></div>
          <HistoricalTankChart :series="series" />
        </template>
        <p v-else class="section-unavailable">Для цього резервуара немає підтверджених точок історії.</p>
      </template>
      <p v-else class="section-unavailable">{{ emptyMessage }}</p>
    </div>
  </section>
</template>
